import Cookies from 'js-cookie'
import { TokenStorage } from './types'

/** Must not exceed the server-side refresh token TTL, or the cookie survives a
 *  session the API has already dropped and the user is bounced to login on the
 *  first request. See `*_USER_REFRESH_TOKEN_TTL` in `secrets/backend/*.env`. */
const DEFAULT_REFRESH_TOKEN_COOKIE_DAYS = 30

export class CookieTokenStorage implements TokenStorage {
  private accessTokenKey: string
  private refreshTokenKey: string
  private refreshTokenCookieDays: number

  constructor(prefix: string = '', refreshTokenCookieDays: number = DEFAULT_REFRESH_TOKEN_COOKIE_DAYS) {
    this.accessTokenKey = prefix ? `${prefix}-accessToken` : 'accessToken'
    this.refreshTokenKey = prefix ? `${prefix}-refreshToken` : 'refreshToken'
    this.refreshTokenCookieDays = refreshTokenCookieDays
  }

  getAccessToken(): string | undefined {
    return Cookies.get(this.accessTokenKey)
  }

  getRefreshToken(): string | undefined {
    return Cookies.get(this.refreshTokenKey)
  }

  setTokens(accessToken: string, refreshToken: string): void {
    const expirationTime = this.getTokenExpirationTime(accessToken)
    const expires = expirationTime ? new Date(expirationTime) : undefined

    Cookies.set(this.accessTokenKey, accessToken, { expires })
    Cookies.set(this.refreshTokenKey, refreshToken, { expires: this.refreshTokenCookieDays })
  }

  clearTokens(): void {
    Cookies.remove(this.accessTokenKey)
    Cookies.remove(this.refreshTokenKey)
  }

  private getTokenExpirationTime(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      // The API names the claim `expiration`; `exp` is the RFC 7519 name it
      // also emits. Read both so either backend version works.
      const exp = payload.expiration ?? payload.exp
      return exp ? exp * 1000 : null // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing JWT token:', error)
      return null
    }
  }
}